import { AdElementRenderProps } from '@adflow/ad';
import { AdElementText, FontFace as AdflowFontFace, isTextElement, notEmpty } from '@adflow/types';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export function useFonts(customFonts: AdflowFontFace[]) {
  const installedFonts = useRef<Set<string>>(new Set());
  const [ready, setReady] = useState(false);
  const [fontLoadResults, setFontLoadResults] = useState<
    Array<{
      font: AdflowFontFace;
      result: PromiseSettledResult<void>;
    }>
  >([]);

  const installFont = useCallback((fontFamily: string) => {
    installedFonts.current.add(fontFamily);
  }, []);

  const loadFont = useCallback(
    async (font: AdflowFontFace) => {
      // Create a unique font family name to avoid conflicts with page fonts
      // Prefix with 'adflow-preview-' to ensure isolation
      const uniqueFontFamily = `adflow-preview-${font.fontFamily}`;

      if (installedFonts.current.has(uniqueFontFamily)) {
        return;
      }
      const ff = new FontFace(uniqueFontFamily, `url("${font.src}")`, {
        style: font.style,
        weight: font.weight.toString(),
        stretch: font.stretch || 'normal',
      });

      await ff.load();

      installFont(uniqueFontFamily);

      // Technically this is an exprimental API so the types
      // don't really work
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (document.fonts as any).add(ff);

      return;
    },
    [installFont, installedFonts],
  );

  useEffect(() => {
    let mounted = true;

    (async () => {
      setReady(false);
      const result = await Promise.allSettled(customFonts.map(loadFont));

      if (mounted) {
        setReady(true);
        setFontLoadResults(customFonts.map((f, i) => ({ font: f, result: result[i] })));
      }
    })();

    return () => {
      mounted = false;
    };
  }, [customFonts, loadFont]);

  return [ready, fontLoadResults] as const;
}

const getCustomFontsToLoad = (
  customFonts: AdflowFontFace[],
  textElements: AdElementText[],
): AdflowFontFace[] => {
  const fontFamiliesInUse = textElements
    .map((el) => el.data.style.fontFamily)
    .filter(notEmpty)
    .map((family) => {
      // Remove the 'adflow-preview-' prefix if present to match against customFonts
      return family.startsWith('adflow-preview-') ? family.slice('adflow-preview-'.length) : family;
    });

  return customFonts.filter((ff) => fontFamiliesInUse.some((family) => ff.fontFamily === family));
};

export function useLoadFontsForAd(
  adElements: AdElementRenderProps[],
  customFonts: AdflowFontFace[],
) {
  const fontsToLoad = useMemo(() => {
    const textElements = adElements.filter(isTextElement);
    return getCustomFontsToLoad(customFonts, textElements);
  }, [adElements, customFonts]);

  return useFonts(fontsToLoad);
}
